import {ssrSafeDocument} from '@github-ui/ssr-utils'
import React, {createContext, useEffect, useRef, use, useMemo, useCallback} from 'react'

export type NavigableRefContextType = {
  registerNavigableRef: (ref: React.RefObject<HTMLElement | null>) => void
  unregisterNavigableRef: (ref: React.RefObject<HTMLElement | null>) => void
}

export const NavigableRefContext = createContext<NavigableRefContextType>({
  registerNavigableRef: () => {},
  unregisterNavigableRef: () => {},
})

export function NavigableRefProvider({children}: {children: React.ReactNode}) {
  // Create the registry
  const [navigableRefs, setNavigableRefs] = React.useState<Array<React.RefObject<HTMLElement | null>>>([])
  const registerNavigableRef = useCallback((ref: React.RefObject<HTMLElement | null>) => {
    setNavigableRefs(prevNavigableRefs =>
      prevNavigableRefs.some(r => r === ref)
        ? prevNavigableRefs // don’t register duplicates
        : [...prevNavigableRefs, ref],
    )
  }, [])
  const unregisterNavigableRef = useCallback((ref: React.RefObject<HTMLElement | null>) => {
    setNavigableRefs(prevNavigableRefs => prevNavigableRefs.filter(r => r !== ref))
  }, [])

  // Handle key presses
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.altKey && event.key === 'F6') {
        // Firefox focuses the address bar (instead of running this handler) when Ctrl+F6 is pressed,
        // even though Firefox’s documentation says that behavior is only triggered by F6 alone:
        // https://support.mozilla.org/en-US/kb/keyboard-shortcuts-perform-firefox-tasks-quickly#w_miscellaneous_2
        // Ensure this handler runs when Ctrl+F6 is pressed, while preserving default behavior for F6 alone.
        event.preventDefault()
        // Sort navigation Refs just-in-time, according to how they appear in the DOM right now.
        // This accounts for dynamic DOM changes and non-deterministic React render orders.
        const navigableRefsInDOMOrder = navigableRefs
          .filter(ref => ref.current !== null)
          .sort((a, b) => {
            const aNode = a.current as HTMLElement
            const bNode = b.current as HTMLElement
            if (aNode === bNode) {
              return 0
            }
            // Note: `compareDocumentPosition` returns a bitmask, so bitwise AND (i.e. `&`) is necessary to get a meaningful result. Details in https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition#example.
            if (aNode.compareDocumentPosition(bNode) & Node.DOCUMENT_POSITION_PRECEDING) {
              return 1
            }
            return -1
          })

        // Return early if there are no navigable Refs.
        if (navigableRefsInDOMOrder.length === 0) {
          return
        }
        // Get the Ref that is (itself or its descendants) currently focused.
        const activeRefIndex = navigableRefsInDOMOrder.findIndex(r =>
          r.current?.contains(ssrSafeDocument?.activeElement || null),
        )
        // Get the Ref that should receive focus, wrapping if necessary.
        const refToFocus = (
          !event.shiftKey
            ? (navigableRefsInDOMOrder[activeRefIndex + 1] ?? navigableRefsInDOMOrder[0])
            : (navigableRefsInDOMOrder[activeRefIndex - 1] ??
              navigableRefsInDOMOrder[navigableRefsInDOMOrder.length - 1])
        )?.current

        // Return early if the Ref to focus is not valid.
        if (!refToFocus) {
          return
        }
        // If the Ref is not ordinarily focusable, make it focusable temporarily.
        if (
          !['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(refToFocus.tagName) &&
          !refToFocus.hasAttribute('tabindex')
        ) {
          refToFocus.setAttribute('tabindex', '-1')
          const handleBlur = () => {
            refToFocus.removeAttribute('tabindex')
            refToFocus.removeEventListener('blur', handleBlur)
          }
          // eslint-disable-next-line @eslint-react/web-api/no-leaked-event-listener
          refToFocus.addEventListener('blur', handleBlur, {once: true})
        }
        // Focus the Ref.
        refToFocus.focus()
      }
    }
    ssrSafeDocument?.addEventListener('keydown', handleKeyDown)
    return () => {
      ssrSafeDocument?.removeEventListener('keydown', handleKeyDown)
    }
  }, [navigableRefs])

  const navigableRefProviderValue = useMemo(
    () => ({registerNavigableRef, unregisterNavigableRef}),
    [registerNavigableRef, unregisterNavigableRef],
  )

  return <NavigableRefContext value={navigableRefProviderValue}>{children}</NavigableRefContext>
}

export function useNavigableRef<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const {registerNavigableRef, unregisterNavigableRef} = use(NavigableRefContext)

  useEffect(() => {
    registerNavigableRef(ref)
    return () => {
      unregisterNavigableRef(ref)
    }
  }, [registerNavigableRef, unregisterNavigableRef])

  return ref
}
